import { DispatchProp, connect } from 'react-redux';
import { FaEye, FaLock, FaUser } from 'react-icons/fa';
import React, { FormEvent, PureComponent } from 'react';
import { RootState } from '../redux';
import { searchWorkroomSetting } from '../api/workroom-setting';
import { AuthHeader } from '../components/auth/auth_header';
import Bind from 'lodash-decorators/bind';
import { Logo } from '../components/auth/logo';
import { RouteComponentProps } from 'react-router-dom';
import { RouteLink } from '../components/navigation/route_link';
import { TermMessage } from '../components/auth/term_message';
import styled from '../theme';
import { loginHandle } from '../api/signin';
import { seedItemsOnLogin } from '../api/item';
import { ApplicationContext } from '../ApplicationContext';
import { getCustomerPlanInfo } from '../api/stripe';
import Button from "../ui_components/button/button"
import Checkbox from "../ui_components/checkbox/checkbox";
import FormField from "../ui_components/form/formField";
import ValidationForm, { FormValidationError } from "../ui_components/validation/validationForm";
import { ValidationPopupInput } from "../ui_components/validation/validationPopupInput";
import { writeCachedPricingPermission } from '../helper/pricingPermission';
const LeftIconInput = styled(ValidationPopupInput)`
  flex-direction: row-reverse;

  & > svg {
    margin-right: 0.5em;
  }
`;

// Old styling for signup link
const SignupLink = styled(RouteLink)`
  font-size: 0.8em;
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  flex: 1 1 auto;
`;

const ForgotMessage = styled.div`
  margin: 1.5em 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
`;

// tslint:disable-next-line:no-empty-interface
export interface LoginOwnProps extends RouteComponentProps {}

export interface LoginProps extends LoginOwnProps {
  /**
   * Pre-filled username (if exists)
   */
  email?: string;
}

export class Login extends PureComponent<LoginProps & DispatchProp> {
  static contextType = ApplicationContext;
  private static readonly PENDING_ACCOUNT_STATUS = 'pending';

  public render(): JSX.Element {
    const { email } = this.props;

    return (
      <>
        <Logo />
        <AuthHeader uiSize="1.25em" style={{margin:"0.7em 0px 1em", padding:"0px 0px 10px"}}>
          LOGIN
          <SignupLink to="/signup">New to QUIPA? Join</SignupLink>
        </AuthHeader>
        <ValidationForm
          uiOnSubmit={this.login}
          uiPadding={false}
          uiSubmitFailedMessage="Login failed"
          uiSubmitFailedIcon
          uiReportValidityMode="firstInvalid"
          uiDimmerBlurring={false}
          style={{padding:"0 1em"}}
        >
          <FormField uiRequired uiErrorAlignment="left">
            <LeftIconInput
              id="email"
              name="email"
              required
              minLength={1}
              maxLength={255}
              placeholder="E-Mail"
              defaultValue={email}
              pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
              uiReportOnBlur={false}
            >
              <FaUser />
            </LeftIconInput>
          </FormField>
          <FormField uiRequired uiErrorAlignment="left">
            <LeftIconInput
              id="password"
              name="password"
              type="password"
              required
              minLength={1}
              maxLength={255}
              placeholder="Password"
              uiHideOnClickOutside={true}
              uiReportOnBlur={true}
              className="eye"
            >
              <FaEye
                style={{
                  position: 'absolute',
                  cursor: 'pointer',
                }}
                onClick={this.handleShowPassword}
              />
              <FaLock />
            </LeftIconInput>
          </FormField>
          <TermMessage>
            By logging in, you agree to our{' '}
            <a href="https://www.quipa.com/terms-of-use" target="_blank">
              Terms of Use
            </a>{' '}
            and{' '}
            <a href="https://www.quipa.com/privacy-policy" target="_blank">
              Privacy Policy
            </a>
            .
          </TermMessage>
          <Actions>
            <Checkbox id="remember" defaultChecked>
              Remember me
            </Checkbox>
            <Button uiColor="primary" type="submit">
              Login
            </Button>
          </Actions>
        </ValidationForm>
        <ForgotMessage>
          <RouteLink to="/request_reset">Forgot password?</RouteLink>
        </ForgotMessage>
      </>
    );
  }
  @Bind()
  private handleShowPassword() {
    const eye = document.querySelector('.eye')!.querySelector('input')! as HTMLInputElement;
    if (eye.type === 'password') {
      eye.type = 'text';
    } else {
      eye.type = 'password';
    }
  }

  @Bind()
  private async login(e: FormEvent<HTMLFormElement>): Promise<void> {
    localStorage.setItem('showModal', 'true');
    const { setUser } = this.context;
    const { location, history } = this.props;

    const elements = e.currentTarget.elements as any;
    const email = (elements['email'] as HTMLInputElement).value.trim();
    const password = (elements['password'] as HTMLInputElement).value.trim();
    const remember = (elements['remember'] as HTMLInputElement).checked || false;

    const payload = { email, password, remember };
    const res = await loginHandle(payload);

    if (res.error) {
      if (res.message === 'Email not recognized') {
        throw FormValidationError.fromErrorDictionary({ email: res.message });
      } else if (res.message === 'Password incorrect') {
        throw FormValidationError.fromErrorDictionary({ password: res.message });
      } else if (res.message === 'Your company owner is no longer subscribed.') {
        throw FormValidationError.fromErrorDictionary({ email: res.message });
      } else if (
        res.message === 'Your account has been removed from the team.'
      ) {
        throw FormValidationError.fromErrorDictionary({ email: res.message });
      } else {
        throw new Error(res.message);
      }
    }

    const { account, user, token, refreshToken } = res;

    let item_order = '';
    if (account.type !== Login.PENDING_ACCOUNT_STATUS) {
      try {
        const planInfoRes = await getCustomerPlanInfo(account.id);
        let planInfo = planInfoRes['main']
        item_order =
          planInfo && planInfo.product_metadata && planInfo.product_metadata.item_order
            ? planInfo.product_metadata.item_order.toString()
            : '';
      } catch (error) {
        item_order = '';
      }
    }

    const userObj = {
      email: user.email,
      token,
      refreshToken,
      item_order,
      type: account.type,
      isBeta: account.isBeta,
    };

    setUser({ user: userObj });

    localStorage.setItem('quipa-store', JSON.stringify({ user: userObj }));
    localStorage.setItem('accountId', account.id);
    writeCachedPricingPermission(!!account.canSeePricing);
    if (Number(account.ownerAccountId) > 0) {
      localStorage.setItem('ownerAccountId', String(account.ownerAccountId));
      localStorage.setItem('subaccountCanSeePricing', account.canSeePricing ? '1' : '0');
    } else {
      localStorage.removeItem('ownerAccountId');
      localStorage.removeItem('subaccountCanSeePricing');
    }
    localStorage.setItem('firstName', user.firstName);
    localStorage.setItem('lastName', user.lastName);
    localStorage.setItem('role', user.role);
    localStorage.setItem('avatarId', account.avatarId);
    localStorage.setItem('status', account.type);
    localStorage.setItem('isBeta', account.isBeta.toString());
    localStorage.setItem('createdAt', account.createdAt);
    if (user.logoId) {
      localStorage.setItem('logoId', user.logoId);
    }
    if (account.customerId) {
      localStorage.setItem('customerId', account.customerId);
    }

    if (account.type === Login.PENDING_ACCOUNT_STATUS) {
      history.push('/profile-setup');
      return;
    }

    if (location.state && location.state.redirectAfterLogin) {
      history.push({
        pathname: location.state.redirectAfterLogin,
        search: location.state.redirectAfterLoginSearch,
      });
    } else {
      if (account.loginAllowed) {
        const res1 = await searchWorkroomSetting(account.id);
        if (res1.error) {
          throw new Error(res1.message);
        }
        document.cookie = `access=${res.crispAccessCookie}`;
        localStorage.setItem(`login-event-${account.id}`, 'login' + Math.random());
        await seedItemsOnLogin();

        if (user.role === 'admin') {
          history.push('/admin/all-users');
        }else{
          window.location.reload();
          history.push('/');
        }
      } else {
        history.push('/card');
        window.location.reload();
      }
    }
  }
}

function mapStateToProps(state: RootState, ownProps: LoginOwnProps): LoginProps {
  const user = localStorage.getItem('quipa-store');
  const email = user ? JSON.parse(user).user.email : '';

  return {
    ...ownProps,
    email: email,
  };
}

export const LoginContainer = connect(mapStateToProps)(Login);
